import { useState } from 'react';
import {
  MutationObserver,
  useQuery,
  useQueryClient,
  type MutationObserverOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  GetColumnConfigResponse,
  UpsertColumnConfigInput,
  UpsertColumnConfigResponse,
} from '@erp/rpc';
import {
  SD_TABLE_ID,
  invalidateE844sdQueries,
} from '@/api/rpc/hooks/ship-and-debit/useE844sdTransactions';

export function useUserTableColumnConfig(tableId: string) {
  return useQuery(
    orpc.core.userTableColumnConfigs.get.queryOptions({
      input: { tableId },
    }),
  );
}

/**
 * Map of tableId → queryClient-aware invalidator for the list queries whose
 * server-side `attributes` selection is derived from the column config (via
 * `getListAttributes` on the backend). When the column config for one of
 * these tables changes, the cached list queries must be refetched so newly
 * added columns get data populated. Each invalidator uses the exact
 * queryKeys owned by its module rather than substring matching.
 */
const DEPENDENT_QUERY_INVALIDATORS: Record<
  string,
  (queryClient: QueryClient) => void
> = {
  [SD_TABLE_ID]: invalidateE844sdQueries,
};

const UPSERT_MUTATION_KEY = ['core', 'userTableColumnConfigs', 'upsert'];

/**
 * The layout a save leaves behind, mirroring the server: an omitted `sorting`
 * keeps the saved one, `null` clears it.
 */
export function applyColumnConfigUpsert(
  previous: GetColumnConfigResponse | undefined,
  input: Pick<UpsertColumnConfigInput, 'columns' | 'sorting'>,
): NonNullable<GetColumnConfigResponse> {
  const sorting =
    input.sorting === undefined ? previous?.sorting : input.sorting;
  return sorting == null
    ? { columns: input.columns }
    : { columns: input.columns, sorting };
}

const keyFor = (tableId: string) =>
  orpc.core.userTableColumnConfigs.get.queryKey({ input: { tableId } });

/** Saves of one table's view still running or queued, this one included. */
function pendingSaves(queryClient: QueryClient, tableId: string): number {
  return queryClient.isMutating({
    mutationKey: UPSERT_MUTATION_KEY,
    predicate: ({ state: { variables } }) =>
      typeof variables === 'object' &&
      variables !== null &&
      'tableId' in variables &&
      variables.tableId === tableId,
  });
}

/**
 * Saves a table view. The caller is not subscribed to the save's status:
 * nothing reads it, and a table owner re-rendering when each save settles
 * (every header sort or column drag) would redraw the whole table for nothing.
 */
export function useUpsertUserTableColumnConfig(): {
  mutate: (variables: UpsertColumnConfigInput) => void;
} {
  const queryClient = useQueryClient();
  const [save] = useState(() => {
    const observer = new MutationObserver(
      queryClient,
      upsertMutationOptions(queryClient),
    );
    return {
      mutate: (variables: UpsertColumnConfigInput) => {
        // Failures reload the saved view in onError; the rejection itself is
        // not needed.
        void observer.mutate(variables).catch(() => undefined);
      },
    };
  });
  return save;
}

function upsertMutationOptions(
  queryClient: QueryClient,
): MutationObserverOptions<
  UpsertColumnConfigResponse,
  Error,
  UpsertColumnConfigInput
> {
  return {
    mutationKey: UPSERT_MUTATION_KEY,
    // Saves run one at a time, so the server keeps the last layout clicked.
    scope: { id: 'userTableColumnConfigs' },
    mutationFn: rpcClient.core.userTableColumnConfigs.upsert,
    // Applied before the round trip: a header sort or a toggle in the column
    // dialog shows at once instead of after the save returns.
    onMutate: async (variables) => {
      const queryKey = keyFor(variables.tableId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<GetColumnConfigResponse>(queryKey, (previous) =>
        applyColumnConfigUpsert(previous, variables),
      );
    },
    // No snapshot to restore: a queued save's onMutate runs before the saves
    // ahead of it settle, so the layout it replaced may never have been saved.
    // A later save of this table is still queued: its layout stays showing and
    // its response settles the view. Otherwise reload what the server kept.
    onError: (_error, variables) => {
      if (pendingSaves(queryClient, variables.tableId) <= 1) {
        void queryClient.invalidateQueries({
          queryKey: keyFor(variables.tableId),
          refetchType: 'all',
        });
      }
    },
    onSuccess: (data, variables) => {
      // A later save is still queued: its optimistic layout is newer than
      // this response, so leave it showing.
      if (pendingSaves(queryClient, variables.tableId) <= 1) {
        queryClient.setQueryData<GetColumnConfigResponse>(
          keyFor(variables.tableId),
          data,
        );
      }

      const invalidator = DEPENDENT_QUERY_INVALIDATORS[variables.tableId];
      if (invalidator) invalidator(queryClient);
    },
  };
}
